import React, { useMemo } from 'react';

import TrainingCarouselCard from '@rover/components/src/cards/TrainingCarouselCard';
import type { Locale } from '@rover/constants/src/i18n/lingui.constants';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import SnappingCarousel from '@rover/react-lib/src/components/lists/Carousel/SnappingCarousel';
import { OnPaginateAction } from '@rover/react-lib/src/components/lists/Carousel/utils/onPaginateEvent.types';
import { TrainerSearchResult } from '@rover/rsdk/src/apiClient/latest/model/trainerSearchResult';
import { PetType } from '@rover/types/src/Pet';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';

import PaginationWrapper from '../../../components/lists/Carousel/PaginationWrapper';
import { SeoLocationPageProps } from '../SeoLocation/types';

import PriceTransparencySubheading from './PriceTransparencySubheading';

type Props = {
  searchResults: TrainerSearchResult[];
  onSearchResultCardClick?: () => void;
  displayExpanded?: boolean;
  heading: string;
  onPaginate?: OnPaginateAction;
  onSearchResultCardViewed?: React.ComponentProps<typeof TrainingCarouselCard>['onViewIntersect'];
  rootCarouselRef?: React.RefObject<HTMLElement | null>;
  shouldUsePriceTransparency?: boolean;
  countryConfig: SeoLocationPageProps['countryConfig'];
  locale: Locale;
  petType?: PetType;
};

const TrainingSearchResultsCarousel: React.FC<Props> = ({
  searchResults,
  heading,
  shouldUsePriceTransparency,
  onPaginate,
  onSearchResultCardClick,
  onSearchResultCardViewed,
  countryConfig,
  locale,
  ...props
}: Props) => {
  const subheading = (
    <PriceTransparencySubheading
      priceTransparencyLevel={
        shouldUsePriceTransparency ? PriceTransparencyLevels.full : PriceTransparencyLevels.none
      }
      serviceType={ServiceType.DOG_TRAINING}
      countryConfig={countryConfig}
      petType={PetType.DOG}
    />
  );

  const memoizedSearchResults = useMemo(
    () =>
      searchResults.map((searchResult) => (
        <TrainingCarouselCard
          shouldUsePriceTransparency={shouldUsePriceTransparency}
          key={searchResult.personOpk}
          searchResult={searchResult}
          onClick={onSearchResultCardClick}
          onViewIntersect={onSearchResultCardViewed}
          itemProp="provider"
          locale={locale}
          currencyCode={countryConfig.currencyCode}
          {...props}
        />
      )),
    [
      countryConfig.currencyCode,
      locale,
      onSearchResultCardClick,
      onSearchResultCardViewed,
      props,
      searchResults,
      shouldUsePriceTransparency,
    ]
  );
  return (
    <PaginationWrapper
      heading={heading}
      subheading={subheading}
      onPaginate={onPaginate}
      headingVariant="heading"
      headingSize="400"
    >
      <SnappingCarousel>{memoizedSearchResults}</SnappingCarousel>
    </PaginationWrapper>
  );
};
export default TrainingSearchResultsCarousel;
